// Catalog.ID Client — Recovery Sheet Payload (format v1)
// ============================================================================
// Binary QR payload for Recovery Sheets, per Recovery Kit Addendum §4.2.
// Layout (big-endian, fixed offsets except the length-prefixed label):
//
//   magic 'CIRC' (4) | version (1) | recovery_set_id (16) | threshold_k (1) |
//   total_shares_n (1) | share_index (1) | share_y (44) |
//   bundle_fingerprint (32) | account_fingerprint (32) | created_at u64 (8) |
//   label_len (1) | card_label (≤32 UTF-8) | verification_key (32) |
//   payload_mac (32)
//
// The verification key is embedded on every sheet (§4.2: any single sheet can
// be self-verified without the manifest). The MAC is HMAC-SHA-256 keyed by
// the verification key over all preceding bytes — integrity of the printed
// sheet (transcription damage, cross-set mixups), NOT secrecy: a single sheet's
// share reveals nothing regardless (§4.3, information-theoretic SSS).

import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';

export const CARD_MAGIC = 'CIRC';
export const CARD_VERSION = 1;
export const SHARE_LENGTH = 44;
const FINGERPRINT_LENGTH = 32;
const KEY_LENGTH = 32;
const MAC_LENGTH = 32;
export const MAX_LABEL_BYTES = 32;

export interface RecoveryCardPayload {
    version: typeof CARD_VERSION;
    /** Canonical UUID string identifying the recovery set. */
    recoverySetId: string;
    thresholdK: number;
    totalSharesN: number;
    /** SSS x-coordinate, 1..255. */
    shareIndex: number;
    /** SSS y-coordinates for the 44-byte supersecret random portion. */
    shareY: Uint8Array;
    /** hex-64 — SHA-256 of the bundle this kit was created against. */
    bundleFingerprint: string;
    /** hex-64 — domain-separated account binding (informational, aids UI). */
    accountFingerprint: string;
    /** Unix seconds at kit creation. */
    createdAt: number;
    cardLabel: string;
    /** Per-set verification key (32 bytes) — MAC key, not a secret. */
    verificationKey: Uint8Array;
}

/** Encode a sheet payload, appending the HMAC. */
export function encodeRecoveryCard(p: RecoveryCardPayload): Uint8Array {
    if (p.shareY.length !== SHARE_LENGTH) throw new Error(`share_y must be ${SHARE_LENGTH} bytes`);
    if (p.verificationKey.length !== KEY_LENGTH) throw new Error(`verification key must be ${KEY_LENGTH} bytes`);
    if (p.shareIndex < 1 || p.shareIndex > 255) throw new Error('share_index must be 1..255');
    if (p.thresholdK < 2 || p.thresholdK > p.totalSharesN) throw new Error('invalid threshold');
    const label = new TextEncoder().encode(p.cardLabel);
    if (label.length > MAX_LABEL_BYTES) throw new Error(`card_label exceeds ${MAX_LABEL_BYTES} UTF-8 bytes`);

    const body = new Uint8Array(4 + 1 + 16 + 1 + 1 + 1 + SHARE_LENGTH
        + FINGERPRINT_LENGTH + FINGERPRINT_LENGTH + 8 + 1 + label.length + KEY_LENGTH);
    let o = 0;
    body.set(new TextEncoder().encode(CARD_MAGIC), o); o += 4;
    body[o++] = CARD_VERSION;
    body.set(uuidToBytes(p.recoverySetId), o); o += 16;
    body[o++] = p.thresholdK;
    body[o++] = p.totalSharesN;
    body[o++] = p.shareIndex;
    body.set(p.shareY, o); o += SHARE_LENGTH;
    body.set(hexToBytes(p.bundleFingerprint, FINGERPRINT_LENGTH), o); o += FINGERPRINT_LENGTH;
    body.set(hexToBytes(p.accountFingerprint, FINGERPRINT_LENGTH), o); o += FINGERPRINT_LENGTH;
    writeU64(body, o, p.createdAt); o += 8;
    body[o++] = label.length;
    body.set(label, o); o += label.length;
    body.set(p.verificationKey, o); o += KEY_LENGTH;

    const mac = hmac(sha256, p.verificationKey, body);
    const out = new Uint8Array(body.length + MAC_LENGTH);
    out.set(body, 0);
    out.set(mac, body.length);
    return out;
}

/**
 * Is this QR payload a Recovery Sheet AT ALL? Magic and length only — no MAC,
 * no field parsing. A whole kit PDF holds several kinds of QR (the Account
 * Unlock Sheet's usually comes first), so the PDF scan path uses this to keep
 * turning pages until it reaches a sheet of the kind that flow is about. It
 * must stay MAC-blind: a DAMAGED sheet is still a sheet, and saying so is the
 * verification page's entire job.
 */
export function looksLikeRecoveryCard(bytes: Uint8Array): boolean {
    if (bytes.length < 4) return false;
    return new TextDecoder().decode(bytes.subarray(0, 4)) === CARD_MAGIC;
}

/**
 * Decode and verify a sheet payload. Throws with a specific message on
 * anything malformed — wrong magic/version, truncation, MAC mismatch.
 */
export function decodeRecoveryCard(bytes: Uint8Array): RecoveryCardPayload {
    const minLen = 4 + 1 + 16 + 1 + 1 + 1 + SHARE_LENGTH + FINGERPRINT_LENGTH
        + FINGERPRINT_LENGTH + 8 + 1 + KEY_LENGTH + MAC_LENGTH;
    if (bytes.length < minLen) throw new Error('Not a Catalog.ID Recovery Sheet (payload too short)');
    if (new TextDecoder().decode(bytes.subarray(0, 4)) !== CARD_MAGIC) {
        throw new Error('Not a Catalog.ID Recovery Sheet (bad magic)');
    }
    let o = 4;
    const version = bytes[o++];
    if (version !== CARD_VERSION) throw new Error(`Unsupported Recovery Sheet version ${version}`);
    const recoverySetId = bytesToUuid(bytes.subarray(o, o + 16)); o += 16;
    const thresholdK = bytes[o++];
    const totalSharesN = bytes[o++];
    const shareIndex = bytes[o++];
    const shareY = bytes.slice(o, o + SHARE_LENGTH); o += SHARE_LENGTH;
    const bundleFingerprint = bytesToHex(bytes.subarray(o, o + FINGERPRINT_LENGTH)); o += FINGERPRINT_LENGTH;
    const accountFingerprint = bytesToHex(bytes.subarray(o, o + FINGERPRINT_LENGTH)); o += FINGERPRINT_LENGTH;
    const createdAt = readU64(bytes, o); o += 8;
    const labelLen = bytes[o++];
    if (labelLen > MAX_LABEL_BYTES || bytes.length !== minLen + labelLen) {
        throw new Error('Not a Catalog.ID Recovery Sheet (malformed label)');
    }
    const cardLabel = new TextDecoder().decode(bytes.subarray(o, o + labelLen)); o += labelLen;
    const verificationKey = bytes.slice(o, o + KEY_LENGTH); o += KEY_LENGTH;

    const mac = bytes.subarray(o, o + MAC_LENGTH);
    const expected = hmac(sha256, verificationKey, bytes.subarray(0, bytes.length - MAC_LENGTH));
    if (!constantTimeEqual(mac, expected)) {
        throw new Error('Recovery Sheet integrity check failed — the sheet is damaged or from a different set');
    }

    if (shareIndex < 1) throw new Error('Recovery Sheet has an invalid share index');
    if (thresholdK < 2 || thresholdK > totalSharesN) throw new Error('Recovery Sheet has an invalid threshold');

    return {
        version: CARD_VERSION, recoverySetId, thresholdK, totalSharesN, shareIndex,
        shareY, bundleFingerprint, accountFingerprint, createdAt, cardLabel, verificationKey,
    };
}

/** Bytes of the MAC the printed fingerprint shows. Round 6 (plan §13.2) raised
 *  this from 4 to 8: the fingerprint is now carried by a Code 128 barcode
 *  beside the QR and compared automatically, so it has to be a value worth
 *  trusting rather than a glance-check — 4 bytes collide at ~2^16 sheets by the
 *  birthday bound, 8 bytes at ~2^32. */
const FINGERPRINT_BYTES = 8;

/**
 * Human-readable sheet fingerprint, printed beside the QR and carried by the
 * Code 128 barcode next to it: the first 8 MAC bytes as 16 uppercase hex
 * characters in four groups of four — `XXXX-XXXX-XXXX-XXXX`.
 *
 * The grouping is part of the value: it is what the barcode encodes and what
 * the verification page compares, so anything reading it back must normalise
 * (see `normaliseFingerprint`) rather than assume the dashes survived.
 */
export function cardChecksum(encoded: Uint8Array): string {
    const mac = encoded.subarray(encoded.length - MAC_LENGTH);
    const hex = Array.from(mac.subarray(0, FINGERPRINT_BYTES))
        .map(b => b.toString(16).padStart(2, '0').toUpperCase())
        .join('');
    return (hex.match(/.{1,4}/g) ?? []).join('-');
}

/**
 * Canonical form for comparing two fingerprints: uppercase hex, no separators.
 * A barcode read off paper may lose or gain a dash to a scanner's idea of
 * formatting, and a human may type it in lower case; neither is a mismatch.
 */
export function normaliseFingerprint(value: string): string {
    return value.replace(/[^0-9a-fA-F]/g, '').toUpperCase();
}

/** UUIDv7 for recovery_set_id — time-ordered, per §4.2. */
export function generateRecoverySetId(now: Date = new Date()): string {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const ms = BigInt(now.getTime());
    for (let i = 0; i < 6; i++) bytes[i] = Number((ms >> BigInt(8 * (5 - i))) & 0xffn);
    bytes[6] = (bytes[6] & 0x0f) | 0x70; // version 7
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    return bytesToUuid(bytes);
}

/** Generate a fresh per-set verification key. */
export function generateVerificationKey(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(KEY_LENGTH));
}

// ── helpers ─────────────────────────────────────────────────────────────────

function uuidToBytes(uuid: string): Uint8Array {
    const hex = uuid.replace(/-/g, '');
    if (!/^[0-9a-fA-F]{32}$/.test(hex)) throw new Error(`Invalid UUID: ${uuid}`);
    return hexToBytes(hex, 16);
}

function bytesToUuid(b: Uint8Array): string {
    const h = bytesToHex(b);
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function hexToBytes(hex: string, expectedLen: number): Uint8Array {
    if (!/^[0-9a-fA-F]*$/.test(hex) || hex.length !== expectedLen * 2) {
        throw new Error(`Expected ${expectedLen}-byte hex string`);
    }
    const out = new Uint8Array(expectedLen);
    for (let i = 0; i < expectedLen; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    return out;
}

function bytesToHex(b: Uint8Array): string {
    return Array.from(b).map(x => x.toString(16).padStart(2, '0')).join('');
}

function writeU64(buf: Uint8Array, offset: number, value: number): void {
    let v = BigInt(Math.floor(value));
    for (let i = 7; i >= 0; i--) {
        buf[offset + i] = Number(v & 0xffn);
        v >>= 8n;
    }
}

function readU64(buf: Uint8Array, offset: number): number {
    let v = 0n;
    for (let i = 0; i < 8; i++) v = (v << 8n) | BigInt(buf[offset + i]);
    return Number(v);
}

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++) diff |= a[i] ^ b[i];
    return diff === 0;
}
